import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Icon, ICONS } from '@/lib/icons'
import type { GroupStage, KnockoutBracket, KnockoutMatch, Player } from '@/types/api'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getRoundLabel } from './utils'

interface TournamentMatchHistoryProps {
  bracket: KnockoutBracket
  groupStage?: GroupStage | null
  playersById: Record<string, Player>
}

type DisplayMatch = {
  id: string
  label: string
  playerAName: string
  playerBName: string
  score?: string
  matchTime?: string
  winnerName?: string
  contextLabel: string
}

export function TournamentMatchHistory({ bracket, groupStage, playersById }: TournamentMatchHistoryProps) {
  const { t } = useTranslation()

  const { groupStageMatches, knockoutMatches } = useMemo(() => {
    const resolvePlayerName = (playerId?: string) => {
      if (!playerId) return t('common.unknownPlayer')
      return playersById[playerId]?.displayName ?? t('common.unknownPlayer')
    }

    const formatScore = (scoreA?: number | null, scoreB?: number | null) => {
      if (typeof scoreA !== 'number' || typeof scoreB !== 'number') {
        return undefined
      }
      return `${scoreA} - ${scoreB}`
    }

    const formatMatchTime = (value?: string) => {
      if (!value) return undefined
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return undefined
      }
      return date.toLocaleString()
    }

    const groupMatches: DisplayMatch[] = []
    if (groupStage) {
      for (const group of groupStage.groups ?? []) {
        for (const match of group.matches ?? []) {
          if (!match.completed) continue
          groupMatches.push({
            id: match.id,
            label: group.name ?? t('groupStage.history.groupFallback', 'Group match'),
            contextLabel: t('groupStage.history.groupLabel', { group: group.name ?? t('groupStage.history.group', 'Group') }),
            playerAName: resolvePlayerName(match.playerAId),
            playerBName: resolvePlayerName(match.playerBId),
            score: formatScore(match.scoreA, match.scoreB),
            matchTime: formatMatchTime(match.matchTime),
            winnerName: match.winnerId ? resolvePlayerName(match.winnerId) : undefined,
          })
        }
      }
    }

    const knockout: DisplayMatch[] = []
    const matchesByRound = new Map<number, KnockoutMatch[]>()
    for (const match of bracket.matches) {
      if (!match.completed) continue
      const list = matchesByRound.get(match.roundNumber) ?? []
      list.push(match)
      matchesByRound.set(match.roundNumber, list)
    }

    const orderedRounds = Array.from(matchesByRound.entries()).sort((a, b) => b[0] - a[0])

    for (const [roundNumber, matches] of orderedRounds) {
      matches.sort((a, b) => a.positionInRound - b.positionInRound)
      const roundLabel = getRoundLabel(roundNumber, bracket.totalRounds, t)

      for (const match of matches) {
        const playerAName = resolvePlayerName(match.playerAId)
        const playerBName = resolvePlayerName(match.playerBId)
        const score = formatScore(match.scoreA, match.scoreB)
        const winnerName = match.winnerId ? resolvePlayerName(match.winnerId) : undefined
        knockout.push({
          id: match.id,
          label: roundLabel,
          contextLabel: t('knockout.history.roundLabel', { round: roundLabel, match: match.positionInRound + 1 }),
          playerAName,
          playerBName,
          score,
          matchTime: formatMatchTime(match.matchTime),
          winnerName,
        })
      }
    }

    return { groupStageMatches: groupMatches, knockoutMatches: knockout }
  }, [bracket.matches, bracket.totalRounds, groupStage, playersById, t])

  if (groupStageMatches.length === 0 && knockoutMatches.length === 0) {
    return null
  }

  const renderMatchCard = (match: DisplayMatch) => {
    return (
      <div key={match.id} className="rounded-xl border bg-card/60 p-4 shadow-sm transition hover:shadow-md">
        <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
          <span className="font-semibold">{match.label}</span>
          <span>{match.contextLabel}</span>
        </div>
        <div className="mt-3 flex flex-col gap-2 text-sm font-medium">
          <div className="flex items-center justify-between">
            <span>{match.playerAName}</span>
            {match.score && <span className="font-mono text-base">{match.score}</span>}
            <span>{match.playerBName}</span>
          </div>
          {match.winnerName && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon icon={ICONS.checkCircle} width={14} height={14} className="text-success" />
              <span>{t('knockout.history.winner', { player: match.winnerName })}</span>
            </div>
          )}
          {match.matchTime && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon icon={ICONS.clock} width={14} height={14} />
              <span>{match.matchTime}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon icon={ICONS.tournament} width={18} height={18} />
          {t('knockout.history.title', 'Tournament match history')}
        </CardTitle>
        <CardDescription>
          {t('knockout.history.subtitle', 'All completed matches across group and knockout stages.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {groupStageMatches.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon icon={ICONS.users} width={16} height={16} className="text-primary" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t('knockout.history.groupStage', 'Group stage results')}
              </h3>
            </div>
            <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {groupStageMatches.map(renderMatchCard)}
            </div>
          </div>
        )}

        {groupStageMatches.length > 0 && knockoutMatches.length > 0 && <Separator />}

        {knockoutMatches.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon icon={ICONS.podium} width={16} height={16} className="text-primary" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t('knockout.history.knockoutStage', 'Knockout bracket results')}
              </h3>
            </div>
            <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {knockoutMatches.map(renderMatchCard)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
